import { useCallback, useEffect, useRef, useState } from "react";
import { connect, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import validator from "validator";
import { Loader2, X } from "lucide-react";
import { fetchCompanyDetails } from "../../../../api/action/company.jsx";
import { register, googleSignin } from "../../../../api/action/user.jsx";
import { getDefaultCountryCode, getOperatingLocationCode, getPhonePlaceholder } from "../../../../config/locationConfig.js";
import { InputTextField } from "../../../../components/InuptTextField.jsx";
import StyledTooltip from "../../../../components/StyledTooltip.jsx";
import User from "../../../../model/User.jsx";
import { isKioskSessionActive } from "../../../../domains/flexOffice/kiosk/kioskSession.js";
import { useAuthSession } from "../../../../auth/useAuthSession.js";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";

const toastClasses = {
    error: "border-red-200 bg-red-600 text-white",
    success: "border-emerald-200 bg-emerald-600 text-white",
    info: "border-blue-200 bg-blue-600 text-white",
    warning: "border-amber-200 bg-amber-500 text-white",
};

const readStoredCompanyPrimaryId = () => {
    try {
        return localStorage.getItem("CompPrimaryId") || "";
    } catch {
        return "";
    }
};

const normalizePrimaryId = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const numericValue = Number(value);
    return Number.isSafeInteger(numericValue) ? numericValue : value;
};

const buildRegistrationCompany = (company) => {
    const primaryId = normalizePrimaryId(company?.primaryId || readStoredCompanyPrimaryId());
    return {
        ...(company || {}),
        ...(primaryId ? { primaryId } : {})
    };
};

const ToastMessage = ({ open, severity = "success", message, onClose }) => {
    if (!open || !message) return null;

    return (
        <div className="fixed left-1/2 top-6 z-[60] w-[calc(100%-2rem)] max-w-md -translate-x-1/2">
            <div
                role={severity === "error" ? "alert" : "status"}
                className={`flex items-start gap-3 rounded-xl border px-4 py-3 text-sm shadow-xl ${toastClasses[severity] || toastClasses.info}`}
            >
                <span className="flex-1">{message}</span>
                <button
                    type="button"
                    aria-label="Close message"
                    onClick={onClose}
                    className="rounded p-0.5 text-white/80 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-white/70"
                >
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

const Register = (props) => {
    const [user, setUser] = useState(new User());
    const navigate = useNavigate();
    const { isAuthenticated, isLoggedIn } = useAuthSession();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState("");
    const [registrationSuccess, setRegistrationSuccess] = useState(false);
    const [companyLogoFailed, setCompanyLogoFailed] = useState(false);
    const companyState = useSelector((state) => state.companyReducer.company);
    const [loadedCompany, setLoadedCompany] = useState(null);
    // Kiosk guests register on a shared device — Google sign-up would sign
    // them in with whichever Google account is already active in that
    // browser/session, which isn't appropriate on a public kiosk.
    const isKiosk = isKioskSessionActive();
    const activeCompany = companyState?.primaryId ? companyState : loadedCompany || companyState;
    const registrationCompany = buildRegistrationCompany(activeCompany);
    const companyPrimaryId = registrationCompany?.primaryId || '';
    const operatingLocationCode = getOperatingLocationCode(activeCompany);
    const countryCode = getDefaultCountryCode(activeCompany);
    const phonePlaceholder = getPhonePlaceholder(activeCompany);
    
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailError, setEmailError] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState(false);
    const [passwordError, setPasswordError] = useState(false);
    const [confirmPasswordError, setConfirmPasswordError] = useState(false);
    
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [emailTakenError, setEmailTakenError] = useState(false);
    const [phoneTakenError, setPhoneTakenError] = useState(false);
    
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    
    const [passwordRules, setPasswordRules] = useState("");
    const [googleLoading, setGoogleLoading] = useState(false);
    const googleCredentialHandlerRef = useRef(null);

    // Terms and conditions state
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [termsError, setTermsError] = useState(false);

    const ensureCompanyReady = () => {
        if (companyPrimaryId) return true;

        const message = "Company details are still loading. Please try again in a moment.";
        setError(message);
        setSnackbarMessage(message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return false;
    };

    // Create LegalRequirement object for terms and conditions
    const createLegalRequirement = () => {
        return {
            type: "TERMS_AND_CONDITIONS",
            acceptedDate: new Date().toISOString(),
            accepted: true,
             history: []
        };
    };

    // Create PersonalPreference object with LegalRequirements
    const createPersonalPreference = () => {
        return {
            communicationPreferences: [],
            legalRequirements: [createLegalRequirement()]
        };
    };

    // Handle terms click - exactly like footer navigation
    const handleTermsClick = (e) => {
        e.preventDefault();
        
        const legalDocuments = companyState?.legalDocuments;
        
        if (legalDocuments && legalDocuments.length > 0) {
            const termsDoc = legalDocuments.find(doc => 
                doc.contentSubType?.toLowerCase() === 'terms_and_conditions' ||
                doc.name?.toLowerCase().includes('terms') ||
                doc.contentSubType?.toLowerCase().includes('terms')
            );
            
            if (termsDoc) {
                const policyName = termsDoc.contentSubType?.toLowerCase() || 'terms-and-conditions';
                navigate(`/policy/${policyName}`);
            } else {
                navigate('/policy/terms-and-conditions');
            }
        } else {
            navigate('/policy/terms-and-conditions');
        }
    };

    const handleTermsChange = (event) => {
        const isAccepted = event.target.checked;
        setAcceptedTerms(isAccepted);
        setTermsError(false);

        if (isAccepted) {
            const personalPreference = createPersonalPreference();
            setUser(prevUser => ({
                ...prevUser,
                personalPreference: personalPreference
            }));
        } else {
            setUser(prevUser => {
                const { personalPreference, ...rest } = prevUser;
                return rest;
            });
        }
    };

    // Load Google script (skipped entirely in kiosk mode — no button to wire
    // up, and this also prevents Google's One Tap prompt from popping up).
    useEffect(() => {
        if (isKiosk) return undefined;

        const initializeGoogleOneTap = () => {
            if (!window.google || !window.google.accounts) {
                return;
            }

            try {
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (response) => googleCredentialHandlerRef.current?.(response),
                    auto_select: false,
                    cancel_on_tap_outside: true,
                    context: "signup"
                });

                const googleButtonElement = document.getElementById("googleSignInButton");
                if (googleButtonElement) {
                    const measuredWidth = googleButtonElement.getBoundingClientRect().width || googleButtonElement.offsetWidth;
                    const viewportFallback = typeof window !== "undefined" ? window.innerWidth - 48 : 360;
                    const buttonWidth = Math.min(430, Math.max(280, Math.floor(measuredWidth || viewportFallback || 360)));

                    googleButtonElement.innerHTML = "";
                    window.google.accounts.id.renderButton(
                        googleButtonElement,
                        {
                            type: "standard",
                            theme: "outline",
                            size: "large",
                            text: "signup_with",
                            shape: "rectangular",
                            logo_alignment: "left",
                            width: buttonWidth
                        }
                    );
                }

                setTimeout(() => {
                    if (!(isAuthenticated || isLoggedIn)) {
                        window.google.accounts.id.prompt();
                    }
                }, 1000);

            } catch {
                // Google sign-up is optional; keep manual registration available.
            }
        };

        if (window.google) {
            initializeGoogleOneTap();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            initializeGoogleOneTap();
        };
        script.onerror = () => {
            // Manual registration remains available if the Google script cannot load.
        };
        document.head.appendChild(script);

        return () => {
            const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
            if (existingScript) {
                existingScript.remove();
            }
        };
    }, [isAuthenticated, isKiosk, isLoggedIn]);

    // Handle Google One Tap response
    const handleGoogleCredentialResponse = async (response) => {
        if (!ensureCompanyReady()) return;

        setGoogleLoading(true);
        setError("");

        const idToken = response.credential;

        try {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            const googleEmail = payload.email;
            const givenName = payload.given_name || "";
            const familyName = payload.family_name || "";

            if (!googleEmail) {
                throw new Error("Email not received from Google");
            }

            const signinPayload = {
                email: googleEmail,
                authProvider: "GOOGLE",
                userRoles: ["CUSTOMER"],
                authToken: idToken,
                company: { primaryId: companyPrimaryId },
            };

            try {
                const resultAction = await props.googleSignin(signinPayload);
                
                const isSuccess = 
                    resultAction?.type?.includes('SUCCESS') ||
                    resultAction?.payload?.status === 200 ||
                    resultAction?.status === 200 ||
                    resultAction?.data?.status === 200 ||
                    resultAction?.payload?.message?.toLowerCase().includes("success") ||
                    resultAction?.payload?.message?.toLowerCase().includes("login successful");

                if (isSuccess) {
                    const responseData = resultAction?.payload?.data || 
                                       resultAction?.data || 
                                       resultAction?.payload || 
                                       resultAction;
                    
                    await handleSuccessfulLogin(responseData, googleEmail);
                    return;
                }
            } catch {
                // Continue with registration when the Google account is not already linked.
            }

            await handleGoogleRegistration(googleEmail, givenName, familyName, idToken);

        } catch (err) {
            setError(err?.message || "Google sign in error. Please try again.");
            setSnackbarMessage(err?.message || "Google sign in error. Please try again.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setGoogleLoading(false);
        }
    };

    googleCredentialHandlerRef.current = handleGoogleCredentialResponse;

    const handleGoogleRegistration = async (email, firstName, lastName, idToken) => {
        try {
            const personalPreference = createPersonalPreference();

            const requestData = {
                firstName: firstName || "Google",
                lastName: lastName || "User",
                email: email,
                authProvider: "GOOGLE",
                authToken: idToken,
                company: registrationCompany,
                userRoles: ["CUSTOMER"],
                createdBy: "GOOGLE_OAUTH",
                personalPreference: personalPreference
            };

            const res = await props.register(requestData);
            const duplicateField = getDuplicateField(res);
            const responseData = res?.data || res;
            const roles = responseData?.userRoles || [];
            const isExistingUserWithNewRole = roles.length > 1;

            if (duplicateField && !isExistingUserWithNewRole) {
                showDuplicateMessage(duplicateField, false, "CUSTOMER");
                setGoogleLoading(false);
            } else if (res?.status === "NEW" || res?.data?.status === "NEW" || res?.payload?.status === "NEW" || isExistingUserWithNewRole) {
                if (isExistingUserWithNewRole) {
                    if (duplicateField) {
                        showDuplicateMessage(duplicateField, true, "CUSTOMER");
                    }
                    setGoogleLoading(false);
                    return;
                }

                setRegistrationSuccess(true);
                setSnackbarMessage("Registration successful! Logging you in...");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);

                const responseData = res?.data || res?.payload || res;
                
                setTimeout(() => {
                    handleSuccessfulLogin(responseData, email);
                }, 1000);
            } else {
                setError(res?.message || "Registration failed. Please try again.");
                setSnackbarMessage(res?.message || "Registration failed. Please try again.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                setGoogleLoading(false);
            }
        } catch (err) {
            setError(err.message || "Google registration failed");
            setSnackbarMessage(err.message || "Google registration failed");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setGoogleLoading(false);
        }
    };

    const handleSuccessfulLogin = async (response, emailOverride = null) => {
        const responseData = response?.data || response?.payload?.data || response;
        const userRoles = responseData?.userRoles || [];
        const token = responseData?.accessConfirmationToken || 
                     responseData?.token || 
                     response?.token ||
                     response?.payload?.token;
        const userEmail = emailOverride || responseData?.email;

        try {
            if (token) localStorage.setItem("token", token);
            if (userEmail) localStorage.setItem("email", userEmail);
            
            localStorage.setItem("isLoggedIn", "true");
            localStorage.setItem("userRoles", JSON.stringify(userRoles));
            
            if (responseData) {
                localStorage.setItem("userData", JSON.stringify(responseData));
            }
        } catch (storageError) {
            setError(storageError?.message || "Unable to store login details. Please sign in again.");
            setSnackbarMessage(storageError?.message || "Unable to store login details. Please sign in again.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setGoogleLoading(false);
            return;
        }

        const primaryRole = userRoles[0] || "CUSTOMER";

        if (primaryRole === "CUSTOMER") {
            const pendingBooking = sessionStorage.getItem("pendingBooking");
            setGoogleLoading(false);
            
            if (pendingBooking) {
                try {
                    const bookingData = JSON.parse(pendingBooking);
                    setTimeout(() => {
                        navigate('/selected-product-details', { 
                            state: bookingData,
                            replace: true 
                        });
                    }, 100);
                } catch {
                    setTimeout(() => {
                        navigate('/flex-home', { replace: true });
                    }, 100);
                }
            } else {
                setTimeout(() => {
                    navigate('/flex-home', { replace: true });
                }, 100);
            }
            return;
        }

        setGoogleLoading(false);
        setSnackbarMessage("Login successful! Redirecting...");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        setTimeout(() => {
            navigate('/flex-home', { replace: true });
        }, 1500);
    };

    // Generate password rules tooltip text
    useEffect(() => {
        if (!user.password) return;
        const rules = [
            { text: "At least 8 characters", valid: user.password.length >= 8 },
            { text: "Spaces not allowed", valid: !/\s/.test(user.password) },
            { text: "1 uppercase letter (A-Z)", valid: /[A-Z]/.test(user.password) },
            { text: "1 lowercase letter (a-z)", valid: /[a-z]/.test(user.password) },
            { text: "1 number (0-9)", valid: /[0-9]/.test(user.password) },
            { text: "1 special character (!@#$)", valid: /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/.test(user.password) },
        ];

        const formattedRules = rules
            .map((rule) => `${rule.valid ? "✓" : "✗"} ${rule.text}`)
            .join("\n");

        setPasswordRules(`Password must contain:\n${formattedRules}`);
    }, [user.password]);

    // Fetch company details
    useEffect(() => {
        const fullUrl = window.location.href;
        const currentSubdomain = fullUrl.split("/").slice(0, 3).join("/");
        let isMounted = true;
        fetchCompanyDetails(currentSubdomain)
            .then((company) => {
                if (!isMounted || !company) return;
                setLoadedCompany(company);
                if (company.primaryId) {
                    localStorage.setItem("CompPrimaryId", String(company.primaryId));
                }
            })
            .catch(() => {});

        return () => {
            isMounted = false;
        };
    }, []);

    // Redirect if already logged in
    useEffect(() => {
        if (localStorage.getItem("isLoggedIn")) {
            navigate("/products");
        }
    }, [navigate]);

    // Validation functions
    const getNameError = (name) => {
        const letters = /^[A-Za-z]*$/;
        if (name.length < 1) return "Enter at least 1 character";
        if (!letters.test(name)) return "Alphabetical characters only";
        return "";
    };

    const validatePhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return false;
        const digits = phoneNumber.replace(/\D/g, '');
        return digits.length === 10;
    };

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return "";
        return phoneNumber.replace(/\D/g, "").slice(0, 10);
    };

    const formatPhoneForDisplay = (phoneNumber) => {
        if (!phoneNumber) return "";
        const digits = phoneNumber.replace(/\D/g, '');
        
        if (operatingLocationCode === "US" && digits.length >= 10) {
            const areaCode = digits.substring(0, 3);
            const firstPart = digits.substring(3, 6);
            const secondPart = digits.substring(6, 10);
            return `(${areaCode}) ${firstPart}-${secondPart}`;
        }
        
        return digits;
    };

    const confirmPasswordsMatch = (password, confirmPassword) => {
        if (!password || !confirmPassword) return false;
        return password === confirmPassword;
    };

    // Event handlers
    const handleClickShowPassword = useCallback(() => {
        setShowPassword((prev) => !prev);
    }, []);

    const handleClickShowConfirmPassword = useCallback(() => {
        setShowConfirmPassword((prev) => !prev);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedValue = name === "phoneNumber" ? formatPhoneNumber(value) : value;

        setUser((prevUser) => ({ ...prevUser, [name]: updatedValue }));

        switch (name) {
            case "firstName":
                if (firstNameError) setFirstNameError(getNameError(value));
                break;
            case "lastName":
                if (lastNameError) setLastNameError(getNameError(value));
                break;
            case "email":
                setEmailError(!validator.isEmail(value));
                setEmailTakenError(false);
                break;
            case "phoneNumber":
                setPhoneNumberError(!validatePhoneNumber(value));
                setPhoneTakenError(false);
                break;
            case "password":
                setPasswordError(!validator.isStrongPassword(value));
                if (user.confirmPassword) {
                    setConfirmPasswordError(!confirmPasswordsMatch(value, user.confirmPassword));
                }
                break;
            case "confirmPassword":
                setConfirmPasswordError(!confirmPasswordsMatch(user.password, value));
                break;
            default:
                break;
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;

        if (name === "firstName") {
            setFirstNameError(getNameError(value));
        } else if (name === "lastName") {
            setLastNameError(getNameError(value));
        } else if (name === "email") {
            setEmailError(!validator.isEmail(value));
        } else if (name === "phoneNumber") {
            setPhoneNumberError(!validatePhoneNumber(value));
            setPhoneTakenError(false);
        }
    };

    const checkAllValid = () => {
        if (!acceptedTerms) {
            setTermsError(true);
            setSnackbarMessage("You must accept the terms and conditions to continue.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return false;
        }

        if (!user.firstName || !user.lastName || !user.email || !user.phoneNumber || !user.password || !user.confirmPassword) {
            setSnackbarMessage("Please fill all required fields");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return false;
        }

        if (firstNameError || lastNameError || emailError || phoneNumberError || passwordError || confirmPasswordError) {
            setSnackbarMessage("Please fix the errors in the form");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return false;
        }

        return true;
    };

    const clearFields = () => {
        setUser({
            firstName: "",
            lastName: "",
            email: "",
            phoneNumber: "",
            countryCode: "",
            userRoles: ["CUSTOMER"],
            password: "",
            confirmPassword: "",
        });
        setAcceptedTerms(false);
        setEmailTakenError(false);
        setPhoneTakenError(false);
    };

    const getDuplicateField = (response) => {
        const combinedText = `${response?.message || ""} ${response?.status || ""}`.toLowerCase();

        if (combinedText.includes("email") && (combinedText.includes("already") || combinedText.includes("exists") || combinedText.includes("registered") || combinedText.includes("taken"))) {
            return "email";
        }

        if ((combinedText.includes("phone") || combinedText.includes("mobile")) && (combinedText.includes("already") || combinedText.includes("exists") || combinedText.includes("registered") || combinedText.includes("taken"))) {
            return "phone";
        }

        return "";
    };

    const showDuplicateMessage = (duplicateField, roleAdded = false, userRole = "CUSTOMER") => {
        setEmailTakenError(duplicateField === "email");
        setPhoneTakenError(duplicateField === "phone");

        const baseMessage = duplicateField === "phone"
            ? "This phone number already exists."
            : "This email already exists.";

        const message = roleAdded
            ? `${baseMessage} The ${userRole} role has been added to your existing account. Please sign in using your current credentials.`
            : `${baseMessage} Please sign in instead.`;

        setError(message);
        setSnackbarMessage(message);
        setSnackbarSeverity(roleAdded ? "info" : "error");
        setSnackbarOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (isSubmitting || !checkAllValid()) return;
        if (!ensureCompanyReady()) return;

        if (!user.personalPreference) {
            const personalPreference = createPersonalPreference();
            setUser(prevUser => ({
                ...prevUser,
                personalPreference: personalPreference
            }));
        }

        setIsSubmitting(true);
        setIsLoading(true);

        try {
            if (!confirmPasswordsMatch(user.password, user.confirmPassword)) {
                setConfirmPasswordError(true);
                setSnackbarMessage("Passwords do not match");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                setIsLoading(false);
                return;
            }

            localStorage.removeItem("token");
            localStorage.removeItem("email");
            localStorage.removeItem("isLoggedIn");

            const requestData = {
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                phoneNumber: user.phoneNumber,
                countryCode: countryCode,
                password: user.password,
                company: registrationCompany,
                userRoles: ["CUSTOMER"],
                createdBy: "USER",
                personalPreference: user.personalPreference || createPersonalPreference()
            };

            const res = await props.register(requestData);
            const duplicateField = getDuplicateField(res);
            const responseData = res?.data || res;
            const roles = responseData?.userRoles || [];
            const isExistingUserWithNewRole = roles.length > 1;

            if (duplicateField && !isExistingUserWithNewRole) {
                showDuplicateMessage(duplicateField, false, "CUSTOMER");
            } else if (res?.status === "NEW" || res?.data?.status === "NEW" || isExistingUserWithNewRole) {
                if (isExistingUserWithNewRole) {
                    if (duplicateField) {
                        showDuplicateMessage(duplicateField, true, "CUSTOMER");
                    } else {
                        setSnackbarMessage("You already have an account. The CUSTOMER role has been added. Please sign in using your existing credentials.");
                        setSnackbarSeverity("info");
                        setSnackbarOpen(true);
                    }
                    return;
                }

                setRegistrationSuccess(true);
                setEmailTakenError(false);
                setPhoneTakenError(false);
                clearFields();

                setSnackbarMessage("Registration successful! Please check your email to activate your account.");
                setSnackbarSeverity("success");
                setSnackbarOpen(true);

                setTimeout(() => {
                    // A kiosk guest must stay on the kiosk's own guest-only
                    // sign-in screen, never the public /sign-in — see
                    // FlexOfficeHeader.jsx's goToLogin for why.
                    navigate(isKiosk ? "/kiosk/login" : "/sign-in");
                }, 3000);

            } else {
                setError(res?.message || "Registration failed");
                setSnackbarMessage(res?.message || "An error occurred. Please try again.");
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            }
        } catch (err) {
            setError(err.message || "Registration failed");
            setSnackbarMessage(err.message || "Registration failed");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
            setIsLoading(false);
        }
    };

    const handleSignIn = (e) => {
        e.preventDefault();
        navigate(isKiosk ? "/kiosk/login" : "/sign-in");
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    useEffect(() => {
        if (!snackbarOpen) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setSnackbarOpen(false);
        }, 5000);

        return () => window.clearTimeout(timer);
    }, [snackbarOpen]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 flex items-center justify-center p-4 pt-24 fx-m-page">
            {/* Loading Overlay */}
            {isLoading && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
                        <p className="text-slate-700 font-medium">Creating your account...</p>
                    </div>
                </div>
            )}

            <div className="w-full max-w-6xl fx-m-full-bleed">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200/50">
                    <div className="grid lg:grid-cols-2 min-h-[600px]">
                        {/* Left Side - Welcome Section. Hidden in the native app: on a
                            phone this marketing panel just pushes the actual sign-up
                            form below the fold with nothing to scroll past. */}
                        <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 p-12 flex flex-col justify-center text-white relative overflow-hidden fx-m-hide">
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-32 -translate-y-32"></div>
                                <div className="absolute bottom-0 right-0 w-48 h-48 bg-white rounded-full translate-x-24 translate-y-24"></div>
                            </div>

                            <div className="relative z-10 max-w-md">
                                <div className="mb-12">
                                    {companyState?.logoImageContent?.displayLink && !companyLogoFailed ? (
                                        <img
                                            src={companyState.logoImageContent.displayLink}
                                            alt={`${companyState?.name || "Company"} logo`}
                                            className="w-16 h-16 rounded-2xl bg-white/90 object-contain p-2 mb-6 shadow-md"
                                            onError={() => setCompanyLogoFailed(true)}
                                        />
                                    ) : (
                                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-6 backdrop-blur-sm">
                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                                            </svg>
                                        </div>
                                    )}
                                    <h2 className="text-3xl font-bold mb-4 text-white">Join Our Professional Community</h2>
                                    <p className="text-blue-100 text-lg leading-relaxed">
                                        Create your account and unlock exclusive features. Join thousands of professionals who trust our platform.
                                    </p>
                                </div>

                                <div className="space-y-8">
                                    <div className="flex items-start space-x-4">
                                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold mb-2 text-white">Secure Registration</h4>
                                            <p className="text-blue-100 text-sm">Enterprise-grade security for your data protection.</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-4">
                                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold mb-2 text-white">Quick Setup</h4>
                                            <p className="text-blue-100 text-sm">Get started in minutes with streamlined onboarding.</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-4">
                                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-semibold mb-2 text-white">Terms Agreement</h4>
                                            <p className="text-blue-100 text-sm">Your acceptance of terms is recorded with IP and timestamp.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Side - Form */}
                        <div className="p-12 flex flex-col justify-center fx-m-card">
                            <div className="max-w-md mx-auto w-full fx-m-full-bleed">
                                {/* Header */}
                                <div className="mb-10 text-center">
                                    <h1 className="text-3xl font-bold text-slate-800 mb-3">Create Account</h1>
                                    <p className="text-slate-600">Enter your details to get started</p>
                                </div>

                                {/* Success/Error Messages */}
                                {registrationSuccess && (
                                    <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-emerald-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                            <div>
                                                <p className="text-emerald-800 font-medium text-sm">Registration Successful!</p>
                                                <p className="text-emerald-700 text-xs mt-1">Please check your email to activate your account.</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {error && !registrationSuccess && (
                                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <p className="text-red-800 text-sm">{error}</p>
                                        </div>
                                    </div>
                                )}

                                {!isKiosk && (
                                    <>
                                        {/* Google Sign In Button */}
                                        <div className="mb-6">
                                            <div id="googleSignInButton" className="w-full">
                                                {googleLoading && (
                                                    <div className="flex justify-center items-center py-3 bg-blue-50 rounded-xl">
                                                        <Loader2 className="h-6 w-6 animate-spin text-blue-600" aria-hidden="true" />
                                                        <span className="ml-2 text-blue-600 font-medium">Signing in with Google...</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="relative mb-6">
                                            <div className="absolute inset-0 flex items-center">
                                                <div className="w-full border-t border-slate-300"></div>
                                            </div>
                                            <div className="relative flex justify-center text-sm">
                                                <span className="px-4 bg-white text-slate-500">or create account manually</span>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Form */}
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    {/* Name Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">First Name *</label>
                                            <InputTextField
                                                id="Register-firstname-reddittextfield"
                                                name="firstName"
                                                value={user.firstName}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                required
                                                error={!!firstNameError}
                                                helperText={firstNameError && (
                                                    <span className="text-red-500 text-xs">{firstNameError}</span>
                                                )}
                                                className="w-full"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Last Name *</label>
                                            <InputTextField
                                                id="Register-lastname-reddittextfield"
                                                name="lastName"
                                                value={user.lastName}
                                                onBlur={handleBlur}
                                                onChange={handleChange}
                                                required
                                                error={!!lastNameError}
                                                helperText={lastNameError && (
                                                    <span className="text-red-500 text-xs">{lastNameError}</span>
                                                )}
                                                className="w-full"
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
                                        <InputTextField
                                            id="Register-email-reddittextfield"
                                            name="email"
                                            type="email"
                                            value={user.email}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            required
                                            error={emailError || emailTakenError}
                                            helperText={
                                                emailError ? (
                                                    <span className="text-red-500 text-xs">Please enter a valid email address</span>
                                                ) : emailTakenError ? (
                                                    <span className="text-red-500 text-xs">Email already exists</span>
                                                ) : null
                                            }
                                            className="w-full"
                                        />
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                                        <InputTextField
                                            id="Register-phoneNumber-reddittextfield"
                                            name="phoneNumber"
                                            type="tel"
                                            value={formatPhoneForDisplay(user.phoneNumber)}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            required
                                            error={phoneNumberError || phoneTakenError}
                                            placeholder={phonePlaceholder}
                                            helperText={
                                                phoneNumberError ? (
                                                    <span className="text-red-500 text-xs">Please enter a valid 10-digit phone number</span>
                                                ) : phoneTakenError ? (
                                                    <span className="text-red-500 text-xs">Phone number already exists</span>
                                                ) : null
                                            }
                                            InputProps={{
                                                startAdornment: <span className="text-slate-500 font-medium">{countryCode}</span>,
                                            }}
                                            className="w-full"
                                        />
                                    </div>

                                    {/* Password Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Password *</label>
                                            <StyledTooltip title={passwordRules} placement="top" arrow>
                                                <div>
                                                    <InputTextField
                                                        id="Register-password-reddittextfield"
                                                        name="password"
                                                        type={showPassword ? "text" : "password"}
                                                        value={user.password}
                                                        onChange={handleChange}
                                                        showPassword={showPassword}
                                                        onTogglePassword={handleClickShowPassword}
                                                        error={passwordError}
                                                        helperText={passwordError && (
                                                            <span className="text-red-500 text-xs">Password is not strong enough</span>
                                                        )}
                                                        required
                                                        className="w-full"
                                                    />
                                                </div>
                                            </StyledTooltip>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">Confirm Password *</label>
                                            <InputTextField
                                                id="Register-confirmPassword-reddittextfield"
                                                name="confirmPassword"
                                                type={showConfirmPassword ? "text" : "password"}
                                                value={user.confirmPassword}
                                                onChange={handleChange}
                                                showPassword={showConfirmPassword}
                                                onTogglePassword={handleClickShowConfirmPassword}
                                                error={confirmPasswordError}
                                                helperText={confirmPasswordError && (
                                                    <span className="text-red-500 text-xs">Passwords do not match</span>
                                                )}
                                                required
                                                className="w-full"
                                            />
                                        </div>
                                    </div>

                                    {/* Terms and Conditions Checkbox - Exactly like footer navigation */}
                                    <div>
                                        <label className="flex items-start gap-3 text-sm text-slate-700">
                                            <input
                                                type="checkbox"
                                                checked={acceptedTerms}
                                                onChange={handleTermsChange}
                                                id="Register-terms-checkbox"
                                                className="mt-0.5 h-4 w-4 rounded border-slate-300 focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                                style={{ accentColor: termsError ? "#f44336" : "var(--flex-primary, #3B82F6)" }}
                                            />
                                            <span>
                                                By signing up, you agree to our{" "}
                                                <button
                                                    type="button"
                                                    onClick={handleTermsClick}
                                                    className="cursor-pointer border-none bg-transparent p-0 font-medium text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    Terms and Conditions
                                                </button>
                                                {" "}and{" "}
                                                <button
                                                    type="button"
                                                    onClick={handleTermsClick}
                                                    className="cursor-pointer border-none bg-transparent p-0 font-medium text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    Privacy Policy
                                                </button>
                                                , and confirm that you will use the platform in compliance with applicable laws and policies.
                                            </span>
                                        </label>
                                        {termsError && (
                                            <p className="text-red-500 text-xs mt-1 ml-7">
                                                You must accept the terms and conditions to continue
                                            </p>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        id="Register-signup-button"
                                        disabled={isSubmitting || registrationSuccess || !acceptedTerms}
                                        className={`w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 ${
                                            isSubmitting || registrationSuccess || !acceptedTerms
                                                ? "opacity-50 cursor-not-allowed"
                                                : "hover:shadow-lg transform hover:-translate-y-0.5"
                                        }`}
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3"></div>
                                                Creating Account...
                                            </div>
                                        ) : registrationSuccess ? (
                                            "Account Created ✓"
                                        ) : (
                                            "Create Account"
                                        )}
                                    </button>
                                </form>

                                {/* Sign In Link */}
                                <div className="mt-8 text-center">
                                    <p className="text-slate-600">
                                        Already have an account?{" "}
                                        <button
                                            id="Register-signin-link"
                                            onClick={handleSignIn}
                                            className="text-blue-600 hover:text-blue-700 font-medium hover:underline transition-colors"
                                        >
                                            Sign In
                                        </button>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ToastMessage
                open={snackbarOpen}
                severity={snackbarSeverity}
                message={snackbarMessage}
                onClose={handleCloseSnackbar}
            />
        </div>
    );
};

const mapStateToProps = (state) => ({
    companyData: state?.companyReducer?.company || {},
});

export default connect(mapStateToProps, { register, googleSignin })(Register);
